import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Divider, Row, Col, Typography, message } from 'antd';
import type { PricePlan } from '../../../types/pricePlans';
import { FEATURES } from '../../../constants/features';
import CurrencyInput from '../../common/CurrencyInput';
import type { Money } from '../../../types/common';
import { addPricePlan, updatePricePlan } from '../../../api/pricePlans';

interface PricePlanModalProps {
  open: boolean;
  pricePlan?: PricePlan | null;
  onSuccess: (savedPlan: PricePlan) => void;
  onCancel: () => void;
}

const DEFAULT_PER_UNIT_PRICE: Money = { amount: 1, currency: 'USD' };
const DEFAULT_MAX_UNITS = 50;
const DEFAULT_MAX_EMPLOYEES = 10;
const DEFAULT_MAX_USERS = 5;

const PricePlanModal: React.FC<PricePlanModalProps> = ({ open, pricePlan, onSuccess, onCancel }) => {
  const [form] = Form.useForm<PricePlan & {
    per_unit_price?: Money;
    value?: number;
    monthly_value?: number;
    six_months_value?: number;
    twelve_months_value?: number;
  }>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (pricePlan) {
        form.setFieldsValue({
          ...pricePlan,
          per_unit_price: (pricePlan as any).per_unit_price ?? DEFAULT_PER_UNIT_PRICE,
          max_units: pricePlan.max_units ?? DEFAULT_MAX_UNITS,
          max_employees: pricePlan.max_employees ?? DEFAULT_MAX_EMPLOYEES,
          max_users: pricePlan.max_users ?? DEFAULT_MAX_USERS,
          features: pricePlan.features ?? [],
          is_custom: pricePlan.is_custom ?? false,
        });
      } else {
        form.setFieldsValue({
          per_unit_price: DEFAULT_PER_UNIT_PRICE,
          max_units: DEFAULT_MAX_UNITS,
          max_employees: DEFAULT_MAX_EMPLOYEES,
          max_users: DEFAULT_MAX_USERS,
          features: [],
          is_custom: false,
          value: DEFAULT_PER_UNIT_PRICE.amount * DEFAULT_MAX_UNITS,
          monthly_value: DEFAULT_PER_UNIT_PRICE.amount * DEFAULT_MAX_UNITS,
          six_months_value: DEFAULT_PER_UNIT_PRICE.amount * DEFAULT_MAX_UNITS * 6,
          twelve_months_value: DEFAULT_PER_UNIT_PRICE.amount * DEFAULT_MAX_UNITS * 12,
        });
      }
    }
  }, [open, pricePlan, form]);

  const featureOptions = FEATURES.map(f => ({ label: f.name, value: f.id }));

  const handleValuesChange = (_changed: any, all: any) => {
    const max_units = all.max_units ?? DEFAULT_MAX_UNITS;
    const per_unit_price: Money = all.per_unit_price ?? DEFAULT_PER_UNIT_PRICE;
    const amount = per_unit_price.amount ?? 0;
    const value = max_units * amount;
    
    form.setFieldsValue({
      value,
      monthly_value: value,
      six_months_value: value * 6,
      twelve_months_value: value * 12,
    });
  };

  const value = Form.useWatch('value', form) || 0;
  const perUnitPrice: Money = Form.useWatch('per_unit_price', form) || DEFAULT_PER_UNIT_PRICE;
  const monthlyValue = Form.useWatch('monthly_value', form) || value;
  const sixMonthsValue = Form.useWatch('six_months_value', form) || value * 6;
  const twelveMonthsValue = Form.useWatch('twelve_months_value', form) || value * 12;

  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const planData = {
        name: values.name,
        description: values.description,
        is_custom: values.is_custom,
        per_unit_price: values.per_unit_price || DEFAULT_PER_UNIT_PRICE,
        max_units: values.max_units || DEFAULT_MAX_UNITS,
        max_employees: values.max_employees || DEFAULT_MAX_EMPLOYEES,
        max_users: values.max_users || DEFAULT_MAX_USERS,
        features: values.features || [],
        value: {
          amount: values.value || 0,
          currency: (values.per_unit_price || DEFAULT_PER_UNIT_PRICE).currency,
        },
        six_months_value: {
          amount: values.six_months_value || 0,
          currency: (values.per_unit_price || DEFAULT_PER_UNIT_PRICE).currency,
        },
        twelve_months_value: {
          amount: values.twelve_months_value || 0,
          currency: (values.per_unit_price || DEFAULT_PER_UNIT_PRICE).currency,
        },
      };

      let savedPlan: PricePlan;
      if (pricePlan?.id) {
        await updatePricePlan(pricePlan.id, planData);
        savedPlan = { ...planData, id: pricePlan.id } as PricePlan;
        message.success('Price plan updated successfully');
      } else {
        const newPlanId = await addPricePlan(planData);
        savedPlan = { ...planData, id: newPlanId } as PricePlan;
        message.success('Price plan created successfully');
      }

      onSuccess(savedPlan);
      onCancel(); // Close the modal after successful save
    } catch (error) {
      message.error('Failed to save price plan');
      console.error('Error saving price plan:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={pricePlan ? 'Edit Price Plan' : 'Add Price Plan'}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnHidden
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}> 
          <Input placeholder="Enter price plan name" /> 
        </Form.Item>
        
        <Form.Item name="is_custom" label="Type" rules={[{ required: true }]}> 
          <Select 
            options={[
              { value: false, label: 'Standard' }, 
              { value: true, label: 'Custom' }
            ]} 
            placeholder="Select type"
          /> 
        </Form.Item>
        
        <Form.Item name="description" label="Description" rules={[{ required: true }]}> 
          <Input.TextArea placeholder="Enter description" /> 
        </Form.Item>
        
        <Divider orientation="left">Feature Limits</Divider>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="per_unit_price" label="Per Unit Price" rules={[{ required: true }]}> 
              <CurrencyInput 
                value={perUnitPrice} 
                onChange={(val) => {
                  form.setFieldsValue({ per_unit_price: val });
                  handleValuesChange(null, form.getFieldsValue());
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="max_units" label="Max Units" rules={[{ required: true }]}> 
              <Input 
                type="number" 
                min={0} 
                style={{ width: '100%' }} 
                placeholder="Enter max units"
                onChange={(e) => {
                  form.setFieldsValue({ max_units: Number(e.target.value) });
                  handleValuesChange(null, form.getFieldsValue());
                }}
              /> 
            </Form.Item>
          </Col>
        </Row>
        
        <Form.Item name="value" label="Total Value (auto-calculated)">
          <Input type="number" style={{ width: '100%' }} value={value} readOnly disabled />
        </Form.Item>
        
        <Form.Item name="monthly_value" hidden>
          <Input type="number" />
        </Form.Item>
        <Form.Item name="six_months_value" hidden>
          <Input type="number" />
        </Form.Item>
        <Form.Item name="twelve_months_value" hidden>
          <Input type="number" />
        </Form.Item>
        
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Typography.Text strong>Monthly:</Typography.Text> <br />
            <Typography.Text>${monthlyValue.toFixed(2)}</Typography.Text>
          </Col>
          <Col span={8}>
            <Typography.Text strong>6 Months Upfront:</Typography.Text> <br />
            <Typography.Text>${sixMonthsValue.toFixed(2)}</Typography.Text>
          </Col>
          <Col span={8}>
            <Typography.Text strong>1 Year Upfront:</Typography.Text> <br />
            <Typography.Text>${twelveMonthsValue.toFixed(2)}</Typography.Text>
          </Col>
        </Row>
        
        <Divider orientation="left">Limits</Divider>
        
        <Form.Item name="max_employees" label="Max Employees" rules={[{ required: true }]}> 
          <Input 
            type="number" 
            min={0} 
            style={{ width: '100%' }} 
            placeholder="Enter max employees"
          /> 
        </Form.Item>
        
        <Form.Item name="max_users" label="Max Users" rules={[{ required: true }]}> 
          <Input 
            type="number" 
            min={0} 
            style={{ width: '100%' }} 
            placeholder="Enter max users"
          /> 
        </Form.Item>
        
        <Form.Item name="features" label="Features">
          <Select 
            mode="multiple" 
            options={featureOptions}
            placeholder="Select features"
            allowClear
          /> 
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PricePlanModal; 